import {
  H2,
  H3,
  LeaderGroupCombobox,
  ModuleHeader,
  UserCombobox,
} from '@/components'
import {
  ExportButton as BaseButton,
  Checkbox,
  MonthPicker,
} from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { ExportActivitiesModal } from '@/modules/activities'
import { Field, Module, ModuleOptionsValue } from '@/modules/companies'
import type {
  ActivitiesWithReceiptCoolumnNode,
  AllDynamicColumnsQuery,
  AllDynamicColumnsQueryVariables,
} from '@/modules/dynamic_tables'
import {
  ALL_DYNAMIC_COLUMNS_QUERY,
  ColumnNames,
  DynamicColumnType,
} from '@/modules/dynamic_tables'
import { AddonOverview } from '@/modules/salary/AddonOverview'
import { SimpleTimesheetsTable } from '@/modules/timesheets/components/SimpleTimesheetsTable'
import { format, getWeek } from '@/utils/date-fns'
import { asNullableString, asStringArrayOrEmpty } from '@/utils'
import {
  useAdmin,
  useAutoSelectSingleLeaderGroup,
  useBreadcrumbs,
  useCompany,
  useLeaderGroups,
  useModuleConfig,
  useOnErrorAuto,
  useParam,
  usePermissions,
  useInitializeUserFilter,
  useUser,
} from '@/utils/hooks'
import { useUserQuery } from '@/utils/hooks/useUser'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  addMonths,
  endOfDay,
  endOfMonth,
  formatISO,
  parseISO,
  setDate,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import { isMobile, isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import type { ExportActivitiesForm, ExtendedWeekReport, WeekReport } from '..'
import ToggleSwitch from '../../../components_deprecated/Toggle/ToggleSwitch'
import { PageType } from '../../common/consts'
import { ExportType } from '../../exports'
import { ActivitiesMonthSummaries, ActivitiesWeekTable } from '../components'
import { CheckmarkDiv } from '../components/overview/CheckmarkDiv'
import { useActivityApprovedHandlers } from '../hooks'
import { useActivityExportMutations } from '../mutations.hooks'
import { DRIVER_ACTIVITY_PERIOD_REPORT_QUERY } from '../queries'
import { SimpleCommentTable } from '../components/overview/SimpleCommentsTable'
import { useNavigate } from 'react-router'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
  position: relative;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const Controls = styled(Card)`
  display: flex;
  align-items: stretch;

  margin-bottom: 1rem;
  padding: 0;

  section {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 1rem;
    padding: 1rem;
  }

  .export-wrapper {
    display: flex;
    align-items: center;
    padding: 1rem;
    border-left: 1px solid ${props => props.theme.colors.gray8};
  }

  ${props => props.theme.media.largePad} {
    display: flex;
    flex-direction: column;

    .export-wrapper {
      border-left: 0;
      justify-content: flex-end;
    }
  }

  ${props => props.theme.media.custom({ max: '1050px' })} {
    section + section {
      justify-content: flex-start;
    }

    .export-wrapper {
      justify-content: flex-start;
    }
  }

  ${props => props.theme.media.mobile} {
    display: flex;
    flex-direction: column;

    section:first-of-type {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }

    .export-wrapper {
      flex: 0 0 auto;
      max-width: none;
      border-left: 0;
      justify-content: flex-end;
      padding-top: 0;
    }
  }
`
const ExportButton = styled(BaseButton)`
  ${props => props.theme.media.mobile} {
    padding: 1rem !important;
    text-align: center;
  }

  ${props => props.theme.media.mediumScreen} {
    padding: 1rem 0.4rem 1rem 2rem !important;

    span {
      display: none;
    }
  }
`
const WorkDayReportFilter = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 300px;
  padding: 1rem;

  h3 {
    margin: 0;
  }

  margin: 1rem 0 0 0;
`

const TypeCheckboxes = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`

const ContainerRadioButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${props => props.theme.colors.gray5};
  }
`

interface FilterForm {
  user: string
  leaderGroup: null | string
  leaderGroupUsers: string[]
  month: Date
  salaryMode: boolean
  useWorkDayReport: boolean
  showDayRest: boolean
  showWeekRest: boolean
}

interface ActivitiesOverviewProps {}

export const ActivitiesOverview: React.FC<ActivitiesOverviewProps> = () => {
  const translations = useTranslate({
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    weekN: ['common.week-n', { n: 0 }],

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'activities.sum-overtime',
    available: 'activities.availability-time',

    toast: {
      mailSuccess: 'exports.success-email-process',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryInterval: 'common.salary-interval',
    month: 'common.month',
    user: 'common.user',
    leaderGroups: 'companies.leader-groups',
    chooseGroup: 'common.choose-group',
  })

  const userId = useParam('userId', true)
  const navigate = useNavigate()

  const { handler: approveHandler } = useActivityApprovedHandlers()

  const { setOverride } = useBreadcrumbs()
  const addPrompt = usePrompt()
  const { hasPermissions, hasAnyPermissions } = usePermissions()
  const company = useCompany()
  const me = useUser()
  const admin = useAdmin()
  const canAdd = hasPermissions(
    PERMISSIONS.companies.export.exportReports,
    true
  )

  const canViewSalaryMode = hasPermissions(
    PERMISSIONS.salary.view.salaryMode,
    false
  )

  const { getModuleOptionStatus: getModuleOptionStatusTimesheets } =
    useModuleConfig(Module.TIMESHEETS)
  const salaryModuleActive = useModuleConfig(Module.SALARY).moduleActive
  const timesheetMergeActive = getModuleOptionStatusTimesheets(
    ModuleOptionsValue.MERGE_TIMESHEETS_WITH_ACTIVITIES
  )
  const [filterStorageStateLoading, setFilterStorageStateLoading] =
    useState(true)
  const { getModuleOptionStatus } = useModuleConfig(Module.ACTIVITIES)
  const hasReceiptFunctionality = getModuleOptionStatus(
    ModuleOptionsValue.ACTIVITIES_RECEIPT_FUNCTIONALITY
  )
  const isMe = me?.id === userId
  const canCheck =
    (isMe &&
      hasPermissions(PERMISSIONS.activities.approve.driveractivity, true)) ||
    (!isMe &&
      hasPermissions(PERMISSIONS.activities.approve.othersdriveractivity, true))
  const canSeeReceipt =
    (admin || canCheck || userId === me.id) && hasReceiptFunctionality
  const canSeeOthers = hasPermissions(
    PERMISSIONS.activities.change.driveractivity,
    true
  )
  const canSeeAddonOverview = hasAnyPermissions(
    [
      PERMISSIONS.salary.view.timeaddon,
      PERMISSIONS.salary.view.fixedaddon,
      PERMISSIONS.salary.view.timeoverride,
    ],
    false
  )
  const onErrorAuto = useOnErrorAuto()
  const leaderGroups = useLeaderGroups(Module.ACTIVITIES)
  const hasLeaderGroups =
    leaderGroups.hasAccess || leaderGroups.myLeaderGroups.length > 0

  const defaultFilterValues = useMemo(
    () => ({
      user: userId ?? me.id,
      leaderGroup: null,
      leaderGroupUsers: [],
      month: new Date(),
      salaryMode: false,
      useWorkDayReport: false,
      showDayRest: false,
      showWeekRest: false,
    }),
    [userId, me.id]
  )

  const {
    formValues: filter,
    updateForm,
    formChangeHandler: handler,
    updateInitialValues,
    loadStoredFormState,
  } = useForm<FilterForm>({
    values: defaultFilterValues,
    config: {
      callbacks: {
        user: updateUrl,
      },
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: canSeeOthers,
        storeFormStateName: 'activity-filters',
        sessionStorage: true,
        transformValues: {
          user: val => (typeof val === 'string' && canSeeOthers ? val : ''),
          leaderGroup: asNullableString,
          leaderGroupUsers: asStringArrayOrEmpty,
          month: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          salaryMode: val => {
            return typeof val === 'boolean' ? val : false
          },
          useWorkDayReport: val => {
            return typeof val === 'boolean'
              ? me.isAdministrator
                ? val
                : false
              : false
          },
          showDayRest: val => {
            return typeof val === 'boolean' ? val : false
          },
          showWeekRest: val => {
            return typeof val === 'boolean' ? val : false
          },
        },
      },
    },
  })

  const { user: filterUser, loading: userLoading } = useUserQuery(
    userId,
    Module.ACTIVITIES
  )
  const applyInitialFilterValues = useCallback(
    (
      values: Partial<FilterForm>,
      options?: { runCallbacks?: boolean; runValidation?: boolean }
    ) => {
      updateForm(values, options)
      updateInitialValues(values)
      setFilterStorageStateLoading(false)
    },
    [updateForm, updateInitialValues]
  )

  // Keep Activities user filter aligned with route/store precedence.
  useInitializeUserFilter<FilterForm>({
    enabled: filterStorageStateLoading,
    routeUserId: userId,
    routeUserLoading: userLoading,
    routeUserIsValid: !!filterUser?.id,
    fallbackUserId: me.id,
    loadStoredFormState,
    resolveValues: ({ storedState, initialUser }) => ({
      values: {
        ...defaultFilterValues,
        ...(storedState ?? {}),
        user: initialUser.userId,
        ...(initialUser.source === 'stored'
          ? {}
          : { leaderGroup: null, leaderGroupUsers: [] }),
      },
      options:
        initialUser.source === 'url'
          ? { runCallbacks: false, runValidation: false }
          : { runValidation: false },
    }),
    applyValues: applyInitialFilterValues,
  })

  const [startDateCalculation, endDateCalculation] = useMemo((): [
    Date,
    Date,
  ] => {
    // If salary view is active, we need to set interval according to salary start and end dates.
    if (filter.salaryMode && filterUser && filterUser.salaryWorkTime) {
      const baseDate = startOfDay(filter.month)
      const salaryStart = filterUser.salaryWorkTime?.salaryDateStart ?? 1
      const salaryEnd = filterUser.salaryWorkTime?.salaryDateEnd ?? 1

      const forward = 31 - salaryStart > 15

      // What salary period is connected to which month is based on the salary start date.
      if (forward) {
        const startMonthYearTime = setDate(baseDate, salaryStart)
        const endMonthYearTime =
          salaryStart === 1
            ? endOfMonth(baseDate)
            : setDate(addMonths(baseDate, 1), salaryEnd)

        return [startMonthYearTime, endOfDay(endMonthYearTime)]
      }

      const startMonthYearTime = setDate(addMonths(baseDate, -1), salaryStart)
      const endMonthYearTime = setDate(baseDate, salaryEnd)

      return [startMonthYearTime, endOfDay(endMonthYearTime)]
    } else {
      // If salary view is not active, we need to set interval according to the month.
      const startMonthYearTime = startOfMonth(filter.month)
      const endMonthYearTime = endOfMonth(filter.month)

      return [startMonthYearTime, endMonthYearTime]
    }
  }, [filter.month, filter.salaryMode, filterUser])

  const filterUserId = filter.user || userId || me.id

  const { data: paddedData, loading } = useQuery(
    DRIVER_ACTIVITY_PERIOD_REPORT_QUERY,
    {
      skip: filterStorageStateLoading || userLoading,
      fetchPolicy: 'cache-and-network',
      variables: {
        user: filterUserId,
        dateStart: formatISO(startDateCalculation),
        dateEnd: formatISO(endDateCalculation),
        useWorkDayReport: filter.useWorkDayReport,
      },
      onError: onErrorAuto(),
      onCompleted(data) {
        if (!data) return
        setOverride(data.user.id, data.user.fullName)
      },
    }
  )

  const { data: columnData } = useQuery<
    AllDynamicColumnsQuery<ActivitiesWithReceiptCoolumnNode>,
    AllDynamicColumnsQueryVariables
  >(ALL_DYNAMIC_COLUMNS_QUERY, {
    variables: {
      moduleName: Module.ACTIVITIES,
    },
    onError: onErrorAuto(),
  })

  const { exportActivities, loading: mailLoading } =
    useActivityExportMutations()

  function getColumns(withReceipt: boolean) {
    const columns = columnData?.allDynamicColumns.edges
      .map(edge => edge.node)
      .filter(node => node.enabled)

    if (columns && filter.useWorkDayReport) {
      const firstColumn = columns[0]
      if (firstColumn)
        columns[0] = {
          ...firstColumn,
          name: ColumnNames.TYPE,
          displayName: 'Type',
          columnType: DynamicColumnType.TYPE,
        }
    }
    if (
      !withReceipt &&
      columns?.some(column => column.name === ColumnNames.APPROVED)
    ) {
      return columns.filter(node => !(node.name === ColumnNames.APPROVED))
    } else return columns
  }

  const columns = getColumns(canSeeReceipt)
  const showReceipt =
    canSeeReceipt &&
    columns?.some(column => column.name === ColumnNames.APPROVED)

  const showCommentTable =
    columns?.some(column => column.name === ColumnNames.COMMENT) && canSeeOthers

  function updateUrl(id: string) {
    if (!id) return
    void navigate(`/activities/${id}`, { replace: true })
  }

  const selectedLeaderGroupUsers = useMemo(
    () => new Set(filter.leaderGroupUsers ?? []),
    [filter.leaderGroupUsers]
  )
  const includeMeInUserSelect =
    !filter.leaderGroup ||
    selectedLeaderGroupUsers.size === 0 ||
    selectedLeaderGroupUsers.has(me.id)

  const onLeaderGroupChange = (value: string) => {
    const leaderGroup = value || null
    const selectedGroup = leaderGroups.myLeaderGroups.find(
      group => group.id === leaderGroup
    )
    const leaderGroupUsers = selectedGroup?.users.map(user => user.id) ?? []
    const shouldUnselectUser =
      !!leaderGroup &&
      leaderGroupUsers.length > 0 &&
      !!filter.user &&
      !leaderGroupUsers.includes(filter.user)
    const nextUser = shouldUnselectUser
      ? leaderGroupUsers[0] ?? me.id
      : filter.user || me.id

    updateForm({
      leaderGroup,
      leaderGroupUsers,
      user: nextUser,
    })
  }

  useAutoSelectSingleLeaderGroup({
    canViewOthers:
      canSeeOthers && hasLeaderGroups && !leaderGroups.groupsLoading,
    isAdmin: admin,
    leaderGroup: filter.leaderGroup ?? undefined,
    leaderGroups: leaderGroups.myLeaderGroups,
    loadStoredFormState,
    updateForm,
    onAutoSelect: group => {
      onLeaderGroupChange(group.id)
    },
  })

  async function doExport(values: ExportActivitiesForm) {
    await exportActivities({
      variables: {
        exportType: values.exportType,
        userMail: me.id,
        user: filter.user,
        company: company.id,
        dateStart:
          values.exportType === ExportType.VEHICLE
            ? formatISO(values.date ?? new Date())
            : formatISO(startDateCalculation),
        dateEnd:
          values.exportType === ExportType.VEHICLE
            ? formatISO(values.dateEnd ?? new Date())
            : formatISO(endDateCalculation),
        exportFormat: values.exportFormat,
        exportOption: values.exportOption,
        weekly:
          values.exportType === ExportType.VEHICLE ? false : values.weekly,
        vehicleId: values.vehicle,
        exportMode: values.modeType,
        detailed: values.detailed,
      },
    })
  }

  async function handleExport() {
    const { data } = await addPrompt<ExportActivitiesForm | null>(resolve => (
      <ExportActivitiesModal
        onSubmit={resolve}
        dateStart={startDateCalculation}
        dateEnd={endDateCalculation}
      />
    ))
    if (!data) return

    await doExport(data)
  }

  function handleOnClickWeek(approve: boolean, week: WeekReport) {
    if (!canCheck) return
    return () => {
      const startOfWeek = parseISO(week.periodStart)
      const endOfWeek = parseISO(week.periodEnd)
      return approveHandler.week(
        userId ?? me.id,
        approve,
        startOfWeek,
        endOfWeek
      )
    }
  }

  const monthApproved =
    paddedData?.report.periodReports.every(periodReport =>
      periodReport.dayReports.every(dayReport =>
        startDateCalculation <= parseISO(dayReport.date) &&
        parseISO(dayReport.date) <= endDateCalculation
          ? dayReport?.approved
          : true
      )
    ) ?? false

  function handleOnClickMonth() {
    if (!canCheck) return

    return approveHandler.month(
      userId ?? me.id,
      !monthApproved,
      startDateCalculation,
      endDateCalculation
    )
  }

  const weeks: (ExtendedWeekReport & { week: number; approved: boolean })[] =
    useMemo(() => {
      const typeIncludes = [
        'Work',
        'Drive',
        filter.showDayRest ? 'Day rest' : '',
        filter.showWeekRest ? 'Week rest' : '',
      ]
      const weekReports = paddedData?.report.periodReports ?? []

      return weekReports?.map(weekReport => {
        return {
          ...weekReport,
          approved: weekReport?.dayReports.every(
            dayReport => dayReport?.approved
          ),
          dayReports:
            weekReport?.dayReports.filter(dayReport =>
              dayReport.type ? typeIncludes.includes(dayReport.type) : true
            ) ?? [],
          week: getWeek(parseISO(weekReport.periodStart)),
          sums: {
            timeWorkTotal: weekReport.timeWorkTotal,
            timeDriving: weekReport.timeDriving,
            timeOvertime: weekReport.overtime,
            timeAvailability: weekReport.timeAvailability,
            timeRest: weekReport.timeRest,
            timePause: weekReport.timePause,
            diet: weekReport.driverDiets
              .reduce(
                (acc, diet) => acc + (diet.salaryCode ? diet.partOfDay : 0),
                0
              )
              .toString(),
            salaryDay: weekReport.salaryDays
              .reduce((acc, day) => acc + (day.partOfDay ?? 0), 0)
              .toString(),
            timePaidRest: weekReport.timePaidRest,
            timeOtherWork: weekReport.timeOtherWork,
          },
        }
      })
    }, [
      filter.showDayRest,
      filter.showWeekRest,
      paddedData?.report.periodReports,
    ])

  const showRightSection = admin || canAdd
  const isLoading = loading || mailLoading
  const isSalaryModeLocked = filterUser?.salaryWorkTime?.salaryDateStart === 1

  const shouldRenderActivitiesMonthSummaries =
    paddedData && !loading && !filterStorageStateLoading && !!userId

  return (
    <Wrapper>
      <ModuleHeader
        className="flex-col items-start gap-2"
        contentClassName="items-center justify-between gap-4 w-full"
        title={translations.activities}
        page={PageType.DRIVER_DATA}
      >
        <H2>
          ({format(startDateCalculation, 'dd. MMM')} -{' '}
          {format(endDateCalculation, 'dd. MMM')})
        </H2>

        {showReceipt && (
          <CheckmarkDiv checked={monthApproved} onClick={handleOnClickMonth} />
        )}
      </ModuleHeader>

      <Controls>
        <section>
          {canSeeOthers && (
            <Field>
              <label>{translations.user}</label>
              <UserCombobox
                mode="single"
                value={filter.user}
                clearable={false}
                className={isMobileOnly ? 'w-full' : undefined}
                dropdownPageName={DropdownNames.ACTIVITIES}
                filterLeaderGroup={!admin}
                alwaysInclude={includeMeInUserSelect ? me : null}
                hideItem={item =>
                  !!filter.leaderGroup &&
                  selectedLeaderGroupUsers.size > 0 &&
                  !selectedLeaderGroupUsers.has(item.id)
                }
                onChange={user => updateForm({ user: user || me.id })}
              />
            </Field>
          )}
          {hasLeaderGroups && canSeeOthers && (
            <Field>
              <label>{translations.leaderGroups}</label>
              <LeaderGroupCombobox
                mode="single"
                value={filter.leaderGroup ?? ''}
                module={Module.ACTIVITIES}
                clearable
                placeholder={translations.chooseGroup}
                onChange={onLeaderGroupChange}
              />
            </Field>
          )}
          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={filter.month}
              futureYears={0}
              minWidth={isMobileOnly ? '0' : '380px'}
              width={isMobileOnly ? '100%' : undefined}
              maxWidth={isMobileOnly ? '100%' : '420px'}
              onChange={handler('month')}
            />
          </Field>
          {canViewSalaryMode && !isSalaryModeLocked && (
            <Field className={isSalaryModeLocked ? 'opacity-60 grayscale' : ''}>
              {isMobile ? (
                <ContainerRadioButton>
                  <label>{translations.salaryInterval}</label>
                  <ToggleSwitch
                    value={isSalaryModeLocked ? true : filter.salaryMode}
                    onChange={
                      isSalaryModeLocked ? () => {} : handler('salaryMode')
                    }
                  />
                </ContainerRadioButton>
              ) : (
                <>
                  <label>{translations.salaryInterval}</label>
                  <Checkbox
                    alignLeft={true}
                    checked={isSalaryModeLocked ? true : filter.salaryMode}
                    onChange={handler('salaryMode')}
                    boxSize="3.875rem"
                    padding="2rem"
                    boxBorderRadius="8px"
                    grayed={isSalaryModeLocked}
                    disabled={isSalaryModeLocked}
                  />
                </>
              )}
            </Field>
          )}

          {me.isAdministrator && (
            <Field>
              {isMobile ? (
                <ContainerRadioButton>
                  <label>{translations.workDay}</label>
                  <ToggleSwitch
                    value={filter.useWorkDayReport}
                    onChange={handler('useWorkDayReport')}
                  />
                </ContainerRadioButton>
              ) : (
                <>
                  <label>{translations.workDay}</label>
                  <Checkbox
                    alignLeft={true}
                    checked={filter.useWorkDayReport}
                    onChange={handler('useWorkDayReport')}
                    boxSize="3.875rem"
                    padding="2rem"
                    boxBorderRadius="8px"
                  />
                </>
              )}
            </Field>
          )}
        </section>

        {showRightSection && (
          <div className="export-wrapper">
            <ExportButton
              iconLeftProps={{ icon: 'download' }}
              fullWidth={isMobileOnly}
              disabled={isLoading}
              onClick={handleExport}
            >
              <span>{translations.export}</span>
            </ExportButton>
          </div>
        )}
      </Controls>

      {shouldRenderActivitiesMonthSummaries && (
        <ActivitiesMonthSummaries
          month={filter.month}
          report={paddedData?.report}
          useWorkDayReport={filter.useWorkDayReport}
          showOvertime={columns?.some(
            columns => columns.name === ColumnNames.OVERTIME
          )}
        />
      )}
      {timesheetMergeActive && (
        <SimpleTimesheetsTable
          startDate={startDateCalculation}
          endDate={endDateCalculation}
          userId={filterUserId}
        />
      )}

      {showCommentTable && (
        <SimpleCommentTable
          startDate={startDateCalculation}
          endDate={endDateCalculation}
          userId={filterUserId}
        />
      )}

      {canSeeAddonOverview && !salaryModuleActive && filter.user && (
        <AddonOverview
          userId={filterUserId}
          datetimeStart={startDateCalculation}
          datetimeEnd={endDateCalculation}
        />
      )}

      {filter.useWorkDayReport && (
        <WorkDayReportFilter>
          <H3>{translations.showRest}</H3>

          <TypeCheckboxes>
            <Checkbox
              checked={filter.showDayRest}
              onChange={handler('showDayRest')}
              label={translations.dayRest}
            />

            <Checkbox
              checked={filter.showWeekRest}
              onChange={handler('showWeekRest')}
              label={translations.weekRest}
            />
          </TypeCheckboxes>
        </WorkDayReportFilter>
      )}

      <div>
        {weeks?.map(week => (
          <React.Fragment key={week.periodStart}>
            <Header>
              <H2 className="mt-6 mb-4 ml-2">
                {translations.weekN({ n: week.week })}
              </H2>

              {showReceipt && (
                <CheckmarkDiv
                  checked={week.approved}
                  onClick={handleOnClickWeek(!week.approved, week)}
                />
              )}
            </Header>

            <ActivitiesWeekTable
              week={week}
              dateStart={startDateCalculation}
              dateEnd={endDateCalculation}
              userId={filter.user}
              columns={columns}
              detailedDate={filter.useWorkDayReport}
            />
          </React.Fragment>
        ))}
      </div>
    </Wrapper>
  )
}
